import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  PatientStateEvent,
  ProviderStatusChangedEvent,
  ProviderWaitingRoomLink,
  QueueSnapshotEvent,
  SseErrorEvent,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
  WaitingRoomQueueState,
  WaitingRoomSseConnectionState,
} from '../types';

export const TERMINAL_WAITING_ROOM_STATES: readonly WaitingRoomQueueState[] = [
  'COMPLETED',
  'REMOVED',
  'ABANDONED',
];

const isTerminalState = (state: WaitingRoomQueueState) =>
  TERMINAL_WAITING_ROOM_STATES.includes(state);

const waitingRoomEntryAdapter = createEntityAdapter<WaitingRoomEntry, string>({
  selectId: entry => entry.waiting_room_queue_entry_id,
  sortComparer: (a, b) => {
    const aCheckedIn = a.checked_in_at ?? '';
    const bCheckedIn = b.checked_in_at ?? '';
    return aCheckedIn.localeCompare(bCheckedIn);
  },
});

export interface WaitingRoomState extends ReturnType<
  typeof waitingRoomEntryAdapter.getInitialState
> {
  providerAvailability: WaitingRoomProviderAvailability;
  link: ProviderWaitingRoomLink | null;
  sseConnectionState: WaitingRoomSseConnectionState;
  error: string | null;
  lastSnapshotAt: number | null;
  lastEventAt: number | null;
}

const initialState: WaitingRoomState = waitingRoomEntryAdapter.getInitialState({
  providerAvailability: 'offline',
  link: null,
  sseConnectionState: 'idle',
  error: null,
  lastSnapshotAt: null,
  lastEventAt: null,
});

export const waitingRoomSlice = createSlice({
  name: 'waitingRoom',
  initialState,
  reducers: {
    linkReceived: (state, action: PayloadAction<ProviderWaitingRoomLink>) => {
      state.link = action.payload;
    },
    queueSnapshotReceived: (state, action: PayloadAction<QueueSnapshotEvent>) => {
      waitingRoomEntryAdapter.setAll(
        state,
        action.payload.entries.filter(entry => !isTerminalState(entry.current_state)),
      );
      state.providerAvailability = action.payload.provider_status;
      state.error = null;
      state.lastSnapshotAt = Date.now();
      state.lastEventAt = state.lastSnapshotAt;
    },
    providerStatusChanged: (state, action: PayloadAction<ProviderStatusChangedEvent>) => {
      state.providerAvailability = action.payload.status;
      state.lastEventAt = Date.now();
    },
    availabilityChanged: (state, action: PayloadAction<WaitingRoomProviderAvailability>) => {
      state.providerAvailability = action.payload;
      state.lastEventAt = Date.now();
    },
    patientStateChanged: (state, action: PayloadAction<PatientStateEvent>) => {
      const { waiting_room_queue_entry_id: entryId, current_state: currentState } = action.payload;

      if (isTerminalState(currentState)) {
        waitingRoomEntryAdapter.removeOne(state, entryId);
      } else {
        waitingRoomEntryAdapter.updateOne(state, {
          id: entryId,
          changes: { current_state: currentState },
        });
      }

      state.lastEventAt = Date.now();
    },
    sseConnecting: state => {
      state.sseConnectionState = 'connecting';
      state.error = null;
    },
    sseConnected: state => {
      state.sseConnectionState = 'open';
      state.error = null;
    },
    sseReconnecting: (state, action: PayloadAction<string | undefined>) => {
      state.sseConnectionState = 'reconnecting';
      state.error = action.payload ?? null;
    },
    sseDisconnected: state => {
      state.sseConnectionState = 'idle';
    },
    sseErrorReceived: (state, action: PayloadAction<SseErrorEvent>) => {
      state.sseConnectionState = 'error';
      state.error = action.payload.error;
      state.lastEventAt = Date.now();
    },
    waitingRoomErrorReceived: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    resetWaitingRoom: () => initialState,
  },
});

export const waitingRoomActions = waitingRoomSlice.actions;
export const waitingRoomEntrySelectors = waitingRoomEntryAdapter.getSelectors();

export default waitingRoomSlice.reducer;
